// Adapter: raw wire shape (flat Entity<Work> output from EntityController)
// -> nested cluster shape the SPA components were written against.
//
// The components expect cluster shapes (pkg.primary.scoutSlug,
// pkg.install.url, pkg.sightings.scoutSlugs[]) per the legacy ADR-0033
// nested-cluster API design. Our new backend serves flat fields directly
// off the Work entity. This adapter bridges the two without forcing
// either side to change.
//
// If we later decide the backend should serve the nested shape natively
// (by introducing a DTO + controller projection), delete this adapter
// and remove the wrapping in client.ts. Components keep their imports.

import type {
    InstallUrlKind,
    MirrorState,
    Work,
    WorkKind,
    WorkStatus,
    WorkSummary,
    Sighting,
    Provider,
} from './types';

/**
 * Wire shape of a Work row served by Koan.Web's EntityController, which
 * camelCase-serializes the C# Work record verbatim. Field names match
 * src/Gposingway/Catalog/Domain/Work.cs as of ADR-0024 (Tranche B): the
 * legacy `primary*` prefixes are gone (Phase 1 trim), `tombstonedAt` is
 * present (Phase 2 lifecycle), and ScoutSlugs/SightingCount are restored
 * for the N:1 projection. Provider-named wire fields finally rotated to
 * Scout in Tranche B.
 */
export interface ApiWork {
    publiclyListed?: boolean;
    id: string;
    name?: string | null;
    author?: string | null;
    description?: string | null;
    kind: WorkKind;
    /** Computed server-side from kind (ADR-0026). Whether Studio can install. */
    installable?: boolean;
    status: WorkStatus;
    discoveredAt: string;
    lastSeenAt: string;
    lastChangedAt: string;
    metadataPinned: boolean;
    /** Sighting that drives the Work's projected display fields. */
    sourceSightingId?: string | null;
    /** Scout slug of the primary Sighting. */
    scoutSlug?: string | null;
    /** Brand id of the primary Sighting's Scout. */
    source?: string | null;
    /** Landing-page URL of the primary Sighting. */
    page?: string | null;
    /** Direct upstream archive URL of the primary Sighting, if any. */
    upstreamArchive?: string | null;
    /** Scout slugs hosting this Work (one per non-Removed sighting's scout). */
    scoutSlugs: string[];
    /** Count of non-Removed Sightings across all sources. */
    sightingCount: number;
    /** Set when the Work transitioned to Tombstoned (every Sighting Removed). */
    tombstonedAt?: string | null;
    /**
     * ADR-0022 §Field-merge "birth date (original release) → min across
     * sources" — set when at least one Sighting's Entry carries an
     * UpstreamCreatedAt. Null when no source supplied one.
     */
    upstreamCreatedAt?: string | null;
    /**
     * ADR-0022 §Field-merge "freshness (last update) → max across sources" —
     * set when at least one active Sighting's Entry carries an
     * UpstreamUpdatedAt. Null when no source supplied one.
     */
    upstreamUpdatedAt?: string | null;
    installUrl?: string | null;
    installUrlKind: InstallUrlKind;
    mirrorSummary?: ApiMirrorSummary | null;
    suppression?: ApiSuppression | null;
    slug?: string | null;
    claimedByUserIds?: string[];
    tags?: ApiTagSet | null;
    media?: ApiWorkMedia | null;
}

interface ApiMirrorSummary {
    state: MirrorState;
    isStale: boolean;
    bytes?: number | null;
    contentType?: string | null;
    readyAt?: string | null;
}

interface ApiSuppression {
    code: string;
    detail?: string | null;
    at: string;
    by: string;
}

interface ApiTagSet {
    public?: { categories?: Record<string, string[]> };
    private?: { categories?: Record<string, string[]> };
    publicTags?: string[];
    privateTags?: string[];
}

interface ApiWorkMedia {
    images?: ApiWorkMediaImages | null;
}

interface ApiWorkMediaImages {
    cover?: string | null;
    additional?: string[] | null;
}

/**
 * Project a wire-shape Work into the nested-cluster `Work`
 * the SPA components consume. Loss-free: every field is preserved or
 * projected to a defensible default (empty media images, null upstream
 * createdAt — the new Work entity carries them on sightings, not on
 * the denormalized Work row).
 */
export function toWork(api: ApiWork): Work {
    return {
        publiclyListed: api.publiclyListed,
        id: api.id,
        name: api.name ?? null,
        author: api.author ?? null,
        description: api.description ?? null,
        kind: api.kind,
        installable: api.installable ?? isInstallableKind(api.kind),
        status: api.status,
        discoveredAt: api.discoveredAt,
        lastSeenAt: api.lastSeenAt,
        source: api.source ?? null,
        tombstonedAt: api.tombstonedAt ?? null,
        primary: {
            sightingId: api.sourceSightingId ?? null,
            metadataPinned: api.metadataPinned,
            scoutSlug: api.scoutSlug ?? null,
            page: api.page ?? null,
            upstreamArchive: api.upstreamArchive ?? null,
        },
        media: {
            // Cover + gallery are deterministic media ids derived by the
            // WorkDenormalizer from the primary Sighting's upstream
            // URLs. The SPA renders transforms via the recipe pipeline at
            // /media/{id}/{recipe} (Koan.Media MediaController) — see the
            // package-card / package-hero / package-sighting recipes in
            // Gposingway.Catalog.Application.CatalogMediaRecipes. Raw bytes
            // resolve through the same /media/{id} surface; the legacy
            // /api/previews/{id} route is retained as a 301 redirect for
            // back-compat with cached / external links only.
            images: {
                cover: api.media?.images?.cover ?? null,
                additional: api.media?.images?.additional ?? [],
            },
        },
        install: {
            url: api.installUrl ?? null,
            kind: api.installUrlKind,
        },
        upstream: {
            // ADR-0022 §Field-merge: Work projects birth-date min and
            // freshness max from attached Sighting entries. Post-B.3 the
            // denormalizer writes these directly onto the Work row.
            createdAt: api.upstreamCreatedAt ?? null,
            updatedAt: api.upstreamUpdatedAt ?? null,
            latestActivityAt: api.lastChangedAt,
        },
        sightings: {
            scoutSlugs: api.scoutSlugs ?? [],
            count: api.sightingCount,
            lastChangedAt: api.lastChangedAt,
        },
        mirrorSummary: api.mirrorSummary
            ? {
                  state: api.mirrorSummary.state,
                  isStale: api.mirrorSummary.isStale,
                  readyAt: api.mirrorSummary.readyAt ?? null,
                  content: {
                      bytes: api.mirrorSummary.bytes ?? null,
                      contentType: api.mirrorSummary.contentType ?? null,
                  },
              }
            : null,
        tags: api.tags
            ? {
                  public: api.tags.public ?? undefined,
                  publicTags: api.tags.publicTags ?? [],
              }
            : null,
        slug: api.slug ?? null,
        claimedByUserIds: api.claimedByUserIds ?? [],
        metadataPinned: api.metadataPinned ?? false,
        suppression: api.suppression
            ? {
                  code: api.suppression.code,
                  detail: api.suppression.detail ?? null,
                  at: api.suppression.at,
                  by: api.suppression.by,
              }
            : null,
        // Canonical server projections supply per-user markers. Anonymous requests have null markers.
        likedByMe: (api as { likedByMe?: boolean | null }).likedByMe ?? null,
        inAnyOfMyCollections:
            (api as { inAnyOfMyCollections?: boolean | null }).inAnyOfMyCollections ?? null,
    };
}

/**
 * Project a wire-shape Work into the slimmer `WorkSummary` used by
 * the catalog list endpoint. Same shape modulo the description /
 * duplicates omission and the legacy 5-axis tags rollup.
 */
export function toWorkSummary(api: ApiWork): WorkSummary {
    const full = toWork(api);
    return {
        publiclyListed: api.publiclyListed,
        id: full.id,
        name: full.name,
        author: full.author,
        kind: full.kind,
        installable: full.installable,
        status: full.status,
        source: full.source,
        primary: {
            scoutSlug: full.primary.scoutSlug,
            page: full.primary.page,
        },
        install: full.install,
        media: full.media,
        sightings: full.sightings,
        upstream: full.upstream,
        lastSeenAt: full.lastSeenAt,
        mirrorSummary: full.mirrorSummary,
        // Multi-axis tags rollup (engine / game) plus ADR-0038 access and
        // ADR-0039 locale axes. Each maps to a per-axis array the catalog
        // filters consume. The legacy `type` tag axis was retired in
        // ADR-0026 (kind is now the single source of truth, read off
        // `kind` / `installable`). Unmapped axes stay empty so chips render
        // but stay inert until data lands.
        tags: {
            engines: getTagAxis(api.tags, 'engine'),
            games: getTagAxis(api.tags, 'game'),
            source: [],
            dependencies: [],
            access: getTagAxis(api.tags, 'access'),
            locales: getTagAxis(api.tags, 'locale'),
        },
        // Server-projected through the shared personalization hook (ADR-0015 Phase 1.1).
        // Anonymous requests get null; authenticated requests get a
        // boolean per the catalog batch query.
        likedByMe: (api as { likedByMe?: boolean | null }).likedByMe ?? null,
        // Server-projected ownership marker; raw claimant IDs are reserved for admin diagnostics.
        claimedByMe: (api as { claimedByMe?: boolean | null }).claimedByMe ?? null,
    };
}

function getTagAxis(tags: ApiTagSet | null | undefined, axis: string): string[] {
    return tags?.public?.categories?.[axis] ?? [];
}

/**
 * Mirror of the server-side `Work.Installable` computed property (ADR-0026):
 * only ReShade-family content is Studio-installable. Used as a defensive
 * fallback when the wire omits `installable`.
 */
function isInstallableKind(kind: WorkKind): boolean {
    return kind === 'Preset' || kind === 'Shader' || kind === 'Pack' || kind === 'Addon';
}

// Sighting + Provider wire shapes are unchanged: the back-end entities
// serialize directly into the legacy shape (or close enough that the
// SPA's existing types match). Re-export identity adapters for symmetry.

export function toProvider(api: Provider): Provider {
    return api;
}

export function toSighting(api: Sighting): Sighting {
    return api;
}
